import { Injectable } from '@angular/core';
import { Asset, Column, System2d, Upright } from '../asset/asset.dto';
import { Inspection } from '../inspection/inspection.dto';
import { Issue, LocalizedRiskLevel } from '../issue/issue.dto';

export type WarehouseSourceKind = 'current' | 'snapshot';

export interface WarehouseSourceRef {
  kind: WarehouseSourceKind;
  divisionId: string;
  /** Set iff kind === 'snapshot'. */
  snapshotId?: string;
}

export interface SystemViewRequest {
  divisionId: string;
  systemId: string;
  /** Drives the RESOLVED branch of the issue scope filter. */
  inspection?: Inspection;
  /** Mirrors SystemCanvasComponent.loadAllIssues. When false, issues are not fetched. */
  includeIssues: boolean;
}

export interface UprightElevationViewRequest {
  divisionId: string;
  /** Assets whose uprights and columns are rendered, in canvas order. */
  assetIds: string[];
  /** Include the asset immediately before this one, as the side-elevation comparison does. */
  previousAssetOf?: string;
  /** Assets whose issues are rendered. The front elevation currently scopes to its left asset. */
  issueAssetId?: string;
  rowIndex?: number;
  inspection?: Inspection;
  includeIssues: boolean;
}

export interface UprightElevationView {
  assets: Asset[];
  uprights: Upright[];
  columns: Column[];
  issues: Issue[];
  priorities: LocalizedRiskLevel[];
  inspection?: Inspection;
  source: WarehouseSourceRef;
}

/**
 * Everything SystemCanvasComponent needs to render one system, already joined and scoped.
 * Both data sources produce this same shape, so the canvas render path stays source-agnostic.
 */
export interface SystemView {
  /** null => the system does not exist in this source (drives systemNotFound). */
  system: System2d;
  /** Sorted by index, clientCode merged. Always a fresh array. */
  uprights: Upright[];
  /** Every rowIndex. Always a fresh array. */
  columns: Column[];
  /** Already scoped to this system plus the resolution/inspection rule. */
  issues: Issue[];
  /** Distinct Column.rowIndex values. */
  columnDepths: number[];
  priorities: LocalizedRiskLevel[];
  inspection?: Inspection;
  source: WarehouseSourceRef;
}

/**
 * Read seam for warehouse geometry, letting the same canvas render either the current
 * Firestore state or a historical snapshot payload.
 *
 * Deliberately not registered as a DI token itself: the choice between implementations is
 * runtime state, so consumers inject WarehouseSourceService and ask it for the active source.
 *
 * Upright-elevation reads share this seam. Warehouse-plan reads remain in the history-aware
 * service layer.
 */
@Injectable()
export abstract class WarehouseDataSource {
  abstract readonly kind: WarehouseSourceKind;

  abstract listSystems(divisionId: string, section?: string | null): Promise<System2d[]>;

  abstract loadSystemView(req: SystemViewRequest): Promise<SystemView>;

  abstract loadUprightElevationView(req: UprightElevationViewRequest): Promise<UprightElevationView>;
}
